import React, { useState } from 'react';
import {
  FiActivity,
  FiAward,
  FiBarChart2,
  FiBookOpen,
  FiClipboard,
  FiDatabase,
  FiFlag,
  FiHome,
  FiMessageSquare,
  FiPieChart,
  FiTag,
  FiUsers
} from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  currentPage: string;
  onPageChange: (page: string) => void;
  onClose: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const MAIN_MENU_ITEMS: MenuItem[] = [
  { id: 'dashboard', label: '대시보드', icon: FiHome, description: '학습 현황' },
  { id: 'exam', label: '시험 응시', icon: FiClipboard, description: '모의시험 시작' },
  { id: 'results', label: '시험 결과', icon: FiBarChart2, description: '점수와 분석' },
  { id: 'ranking', label: '랭킹', icon: FiAward, description: '성적 순위' },
  { id: 'board', label: '게시판', icon: FiMessageSquare, description: '공지와 글' }
];

const ADMIN_MENU_ITEMS: MenuItem[] = [
  { id: 'user-management', label: '사용자 관리', icon: FiUsers, description: '계정과 권한' },
  { id: 'question-bank-management', label: '문제 은행', icon: FiDatabase, description: '문제와 시험' },
  { id: 'coupon-management', label: '쿠폰 관리', icon: FiTag, description: '쿠폰 접근 권한' },
  { id: 'report-management', label: '신고 관리', icon: FiFlag, description: '사용자 신고' },
  { id: 'exam-monitoring', label: '시험 모니터링', icon: FiActivity, description: '진행 중 세션' },
  { id: 'analytics-dashboard', label: '운영 분석', icon: FiPieChart, description: '운영 지표' },
  { id: 'board-management', label: '게시판 관리', icon: FiBookOpen, description: '콘텐츠 관리' },
  { id: 'achievement-management', label: '업적 관리', icon: FiAward, description: '배지와 목표' }
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, currentPage, onPageChange, onClose }) => {
  const { user } = useAuth();
  const [adminExpanded, setAdminExpanded] = useState(true);

  const handleItemClick = (pageId: string) => {
    onPageChange(pageId);
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  const renderMenu = (items: MenuItem[]) => (
    <ul className="sidebar-v2__menu">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <li key={item.id} className="sidebar-v2__item">
            <button
              className={`sidebar-v2__link ${currentPage === item.id ? 'sidebar-v2__link--active' : ''}`}
              onClick={() => handleItemClick(item.id)}
              title={isCollapsed ? item.label : undefined}
            >
              <span className="sidebar-v2__icon"><Icon className="sidebar-v2__icon-svg" /></span>
              {!isCollapsed && (
                <span className="sidebar-v2__text-wrap">
                  <span className="sidebar-v2__label">{item.label}</span>
                  <span className="sidebar-v2__desc">{item.description}</span>
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {isOpen && window.innerWidth <= 768 && <div className="sidebar-v2__overlay" onClick={onClose}></div>}

      <aside className={`sidebar-v2 ${isOpen ? 'sidebar-v2--open' : ''} ${isCollapsed ? 'sidebar-v2--collapsed' : ''}`}>
        <div className="sidebar-v2__content">
          <nav className="sidebar-v2__nav">
            <section className="sidebar-v2__section">
              {!isCollapsed && <h3 className="sidebar-v2__section-title">워크스페이스</h3>}
              {renderMenu(MAIN_MENU_ITEMS)}
            </section>

            {user?.is_admin && (
              <section className="sidebar-v2__section sidebar-v2__section--admin">
                {!isCollapsed && (
                  <button
                    className="sidebar-v2__section-toggle"
                    onClick={() => setAdminExpanded((prev) => !prev)}
                  >
                    <span>관리</span>
                    <span>{adminExpanded ? '-' : '+'}</span>
                  </button>
                )}
                {(adminExpanded || isCollapsed) && renderMenu(ADMIN_MENU_ITEMS)}
              </section>
            )}
          </nav>

          {!isCollapsed && (
            <div className="sidebar-v2__footer">
              <div className="sidebar-v2__user-card">
                <img
                  src={user?.profile_picture || '/default-avatar.svg'}
                  alt="User profile"
                  className="sidebar-v2__user-avatar"
                  onError={(event) => {
                    event.currentTarget.src = '/default-avatar.svg';
                  }}
                />
                <div className="sidebar-v2__user-info">
                  <span className="sidebar-v2__user-name">{user?.name || '사용자'}</span>
                  <span className="sidebar-v2__user-email">{user?.email}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
